import { useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Eyebrow } from '@/components/domain/Eyebrow';
import { useFadeUpOnScroll } from '@/hooks/useFadeUpOnScroll';
import styles from './AuditNoticeSection.module.css';

export const AuditNoticeSection: FC = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useFadeUpOnScroll(ref, { selector: '[data-fade]', stagger: 0.1 });

  return (
    <Section tone="dark" id="audit-notice">
      <Container size="default">
        <div ref={ref}>
          <header className={styles.header}>
            <div data-fade>
              <Eyebrow tone="inverse">{t('home.auditNotice.kicker')}</Eyebrow>
            </div>
            <h2 className={styles.title} data-fade>
              {t('home.auditNotice.title')}
            </h2>
          </header>

          <div className={styles.layout}>
            <article className={styles.email} data-fade>
              <header className={styles.emailHeader}>
                <span className={styles.emailBadge}>{t('home.auditNotice.emailBadge')}</span>
              </header>
              <dl className={styles.metaList}>
                <div className={styles.metaRow}>
                  <dt>{t('home.auditNotice.toLabel')}</dt>
                  <dd>{t('home.auditNotice.toValue')}</dd>
                </div>
                <div className={styles.metaRow}>
                  <dt>{t('home.auditNotice.fromLabel')}</dt>
                  <dd>{t('home.auditNotice.fromValue')}</dd>
                </div>
                <div className={styles.metaRow}>
                  <dt>Subject:</dt>
                  <dd>{t('home.auditNotice.subject')}</dd>
                </div>
              </dl>
              <p className={styles.emailBody}>{t('home.auditNotice.body')}</p>
              <p className={styles.totalLine}>
                {t('home.auditNotice.totalLabel')}:{' '}
                <span className={styles.totalValue}>{t('home.auditNotice.totalValue')}</span>
              </p>
            </article>

            <aside className={styles.fineCard} data-fade>
              <p className={styles.fineLabel}>⚠ {t('home.auditNotice.fineRisk')}</p>
              <p className={styles.fineAmount}>{t('home.auditNotice.fineAmount')}</p>
              <p className={styles.fineCurrency}>{t('home.auditNotice.fineCurrency')}</p>
            </aside>
          </div>

          <p className={styles.caption} data-fade>
            {t('home.auditNotice.caption')}
          </p>
        </div>
      </Container>
    </Section>
  );
};

AuditNoticeSection.displayName = 'AuditNoticeSection';
